// ============================================
// Sidebar Component — Premium Constant Dark Pattern
// Supports role-based theme color highlights
// ============================================

import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logoutUser } from '../../redux/slices/authSlice';

// ── Theme presets (Always on dark background) ────────────
const themes = {
    indigo: { // Admin
        logo: 'from-blue-500 to-blue-600',
        active: 'bg-blue-600/20 text-blue-400 shadow-[inset_4px_0_0_0_rgba(96,165,250,1)] border-transparent',
        badge: 'bg-blue-500/20 text-blue-400',
        avatar: 'from-blue-400 to-blue-500',
    },
    emerald: { // Dispatcher
        logo: 'from-blue-500 to-cyan-600',
        active: 'bg-teal-500/10 text-teal-400 shadow-[inset_4px_0_0_0_rgba(45,212,191,1)] border-transparent',
        badge: 'bg-teal-500/20 text-teal-400',
        avatar: 'from-teal-400 to-cyan-500',
    },
    amber: { // Driver
        logo: 'from-amber-400 to-amber-600',
        active: 'bg-amber-500/15 text-amber-400 shadow-[inset_4px_0_0_0_rgba(251,191,36,1)] border-transparent',
        badge: 'bg-amber-500/20 text-amber-400',
        avatar: 'from-amber-400 to-amber-600',
    },
    teal: { // User 
        logo: 'from-teal-400 to-teal-600',
        active: 'bg-teal-500/10 text-teal-400 shadow-[inset_4px_0_0_0_rgba(45,212,191,1)] border-transparent',
        badge: 'bg-teal-500/20 text-teal-400',
        avatar: 'from-teal-400 to-teal-600',
    },
    violet: { // PA
        logo: 'from-violet-400 to-purple-600',
        active: 'bg-violet-500/15 text-violet-400 shadow-[inset_4px_0_0_0_rgba(139,92,246,1)] border-transparent',
        badge: 'bg-violet-500/20 text-violet-400',
        avatar: 'from-violet-400 to-purple-500',
    },
};

const Sidebar = ({ items, title = 'IdaCab', theme = 'indigo', mobileOpen, setMobileOpen }) => {
    const { user } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const t = themes[theme] || themes.indigo;

    const handleLogout = () => {
        dispatch(logoutUser());
    };

    return (
        <>
            {/* Mobile Overlay */}
            {mobileOpen && (
                <div 
                    className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[55] sm:hidden transition-opacity"
                    onClick={() => setMobileOpen && setMobileOpen(false)}
                />
            )}
            
            <aside className={`
                fixed sm:relative inset-y-0 left-0 z-[60] flex flex-col bg-[#0b1121] text-slate-300 transition-all duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] 
                ${collapsed ? 'w-[80px]' : 'w-[260px]'} 
                ${mobileOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}
                border-r border-slate-800/80 shadow-[10px_0_30px_rgba(0,0,0,0.15)]
            `}>
            {/* Header */}
            <div className={`flex items-center h-[88px] border-b border-slate-800/60 flex-shrink-0 transition-all duration-300 ${collapsed ? 'justify-center px-0' : 'justify-between px-5'}`}>
                {!collapsed && (
                    <div className="flex items-center gap-4 overflow-hidden animate-in fade-in zoom-in duration-300">
                        <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${t.logo} flex items-center justify-center flex-shrink-0 shadow-[0_4px_15px_rgba(0,0,0,0.3)]`}>
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                        </div>
                        <span className="font-black text-xl text-white tracking-[-0.03em] whitespace-nowrap">{title}</span>
                    </div>
                )}
                <button onClick={() => setCollapsed(!collapsed)} className="p-2 rounded-xl hover:bg-slate-800 transition-colors text-slate-500 hover:text-white group">
                    <svg className={`w-4 h-4 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto sidebar-scroll transition-colors">
                {items.map((item, idx) => {
                    if (item.divider) {
                        return (
                            <div key={idx} className="pt-6 pb-2 px-4">
                                {!collapsed && <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] leading-none mb-1">{item.label}</p>}
                                {collapsed && <div className="h-px bg-slate-800/80 w-full" />}
                            </div>
                        );
                    }

                    return (
                        <NavLink
                            key={idx}
                            to={item.path}
                            onClick={() => setMobileOpen && setMobileOpen(false)}
                            end={item.exact}
                            className={({ isActive }) =>
                                `flex items-center gap-4 px-4 py-3.5 rounded-xl text-sm font-bold transition-all duration-200 group border
                ${isActive
                                    ? t.active
                                    : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-100 border-transparent'
                                }`
                            }
                        >
                            <span className={`flex-shrink-0 w-5 h-5 transition-colors`} dangerouslySetInnerHTML={{ __html: item.icon }} />
                            {!collapsed && <span className="whitespace-nowrap tracking-wide">{item.label}</span>}
                            {!collapsed && item.badge && (
                                <span className={`ml-auto px-2 py-0.5 rounded-lg ${t.badge} text-[10px] font-black uppercase tracking-widest leading-none`}>
                                    {item.badge}
                                </span>
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            {/* User Profile Footer */}
            <div className="p-5 border-t border-slate-800/60 bg-[#070b16] flex-shrink-0 transition-colors overflow-hidden">
                <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${t.avatar} flex items-center justify-center text-white text-base font-black flex-shrink-0 shadow-lg`}>
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    {!collapsed && (
                        <div className="flex-1 min-w-0 pr-2">
                            <p className="text-[13px] font-bold text-white truncate leading-tight tracking-wide">{user?.name}</p>
                            <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 leading-none mt-1">{user?.role}</p>
                        </div>
                    )}
                    {!collapsed && (
                        <button onClick={handleLogout} className="p-2.5 rounded-xl text-slate-500 hover:text-white hover:bg-slate-800 transition-all ease-out hover:scale-105 active:scale-95" title="Logout">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </aside>
        </>
    );
};

export default Sidebar;
